import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { format, secondsToMilliseconds } from "date-fns";
import { get } from "../../services/httpClient";
import { PatientMessage } from "core/types/index";
import CONSTANTS from "core/constants";
import { useTranslation } from "react-i18next";
import { apiv2QueryClient } from "../../apiv2QueryClient";

const getPatientMessages = async (patientUserId: number) => {
  const res = await get<{ data: PatientMessage[] }>(
    `psychologist/message/${patientUserId}`,
  );
  return res.data;
};

export type MessageTableRow = {
  id: string;
  title: string;
  messageTemplate: string;
  sentBy: string;
  date: string;
  body: string;
  attachmentKeys?: string[];
  status: {
    isDeleted: boolean;
    isViewed: boolean;
  } | null;
};

export const getPatientMessagesQueryKey = (patientUserId: number) => [
  "patientMessages",
  patientUserId,
];

export const useGetPatientMessages = (patientUserId: number | undefined) => {
  const { t } = useTranslation();

  return useQuery({
    queryKey: getPatientMessagesQueryKey(patientUserId ?? 0),
    queryFn: () =>
      patientUserId ? getPatientMessages(patientUserId) : Promise.resolve([]),
    enabled: !!patientUserId && patientUserId > 0,
    staleTime: secondsToMilliseconds(60),
    refetchInterval: secondsToMilliseconds(60),
    select(data) {
      return data.map<MessageTableRow>((message) => {
        return {
          id: message.messageId,
          title: message?.senderLanguageTitle,
          messageTemplate: t(message?.psychologistMessageTemplateType),
          sentBy: message.psychologistName,
          status: {
            isDeleted: message.deleted ?? false,
            isViewed: message.viewed ?? false,
          },
          date: format(
            new Date(message?.createdAt ?? ""),
            CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
          ),
          attachmentKeys: message?.attachmentKeys || [],
          body:
            message.psychologistMessageTemplateType === "FREE_TEXT_MESSAGE"
              ? message?.description
              : message?.senderLanguageDescription,
        };
      });
    },
  });
};

export const prefetchPatientMessages = async (patientUserId: number) => {
  return apiv2QueryClient.prefetchQuery({
    queryKey: getPatientMessagesQueryKey(patientUserId),
    queryFn: () => getPatientMessages(patientUserId),
    staleTime: secondsToMilliseconds(60),
  });
};
